import { useState, useRef, useEffect } from 'react';
import { automationAPI } from '../api/client';

// ── Info modal ───────────────────────────────────────────────────────────────

const INFO_CONTENT = {
  autoArticle: {
    title: 'Синхронизация ключевых слов фотопоиска',
    sections: [
      {
        heading: 'Что делает',
        text: 'Для каждого поставщика проверяет наличие листа «Автоартикул» в его таблице. Если лист есть — считывает артикул поставщика (столбец A) и ключевое слово (столбец F), находит соответствующий артикул каталога и отправляет пару в API фотопоиска.',
      },
      {
        heading: 'Источник списка поставщиков',
        text: 'Google Sheets · таблица «Прайс-листы» (ID: 1yF59HiByxfYsKTK7QRD4MqDfUn3xJOTqcmhf5m5yvaA) · лист «Список поставщиков»\n\nСтолбец A — название поставщика\nСтолбец B — ID внешней таблицы поставщика',
      },
      {
        heading: 'Лист «Автоартикул» (в таблице поставщика)',
        text: 'Столбец A — артикул поставщика\nСтолбец F — ключевое слово для фотопоиска (kayWord)\n\nОбрабатываются только строки, где столбец F заполнен.',
      },
      {
        heading: 'Лист «ALLDROP ART» (в таблице поставщика)',
        text: 'Столбец A — артикул поставщика (совпадает с «Автоартикул» столбец A)\nСтолбец B — артикул в каталоге товаров ALLDROP (ID для API)',
      },
      {
        heading: 'API фотопоиска',
        text: 'POST http://70.34.255.252:3006/api/UpdatePhotoSearchBot\n\nТело запроса:\n{\n  "art": "<артикул каталога из ALLDROP ART, столбец B>",\n  "kayWord": "<ключевое слово из Автоартикул, столбец F>"\n}',
        mono: true,
      },
      {
        heading: 'Порядок обработки',
        text: '1. Открываем «Список поставщиков» и получаем ID таблиц\n2. Для каждой таблицы проверяем наличие листа «Автоартикул»\n3. Собираем пары (артикул поставщика → ключевое слово)\n4. Открываем «ALLDROP ART», находим артикул каталога\n5. Отправляем запрос в API (задержка 0.2с между запросами)',
      },
    ],
  },
};

function InfoModal({ info, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal automation-info-modal" onClick={e => e.stopPropagation()}>
        <div className="automation-info-header">
          <h3 className="automation-info-title">{info.title}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="automation-info-body">
          {info.sections.map((s, i) => (
            <div key={i} className="automation-info-section">
              <div className="automation-info-heading">{s.heading}</div>
              {s.mono
                ? <pre className="automation-info-pre">{s.text}</pre>
                : <p className="automation-info-text">{s.text}</p>
              }
            </div>
          ))}
        </div>
        <div className="automation-info-footer">
          <button className="btn btn-secondary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

// ── AutoArticleCard ──────────────────────────────────────────────────────────

function AutoArticleCard() {
  const [status,   setStatus]   = useState(null);
  const [running,  setRunning]  = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const r = await automationAPI.autoArticleSyncStatus();
        const d = r.data;
        if (!d || !d.status || d.status === 'idle') return;
        setStatus(d);
        if (d.status === 'running') {
          setRunning(true);
          interval = setInterval(poll, 2000);
          pollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(pollRef.current), []);

  async function poll() {
    try {
      const r = await automationAPI.autoArticleSyncStatus();
      setStatus(r.data);
      if (r.data.status !== 'running') {
        clearInterval(pollRef.current);
        setRunning(false);
      }
    } catch {}
  }

  async function handleStart() {
    setRunning(true);
    setStatus({ status: 'running', progress: 'Запуск…', sent: 0, skipped: 0, total: 0 });
    try {
      await automationAPI.autoArticleSync();
    } catch (e) {
      setStatus({ status: 'error', message: e?.response?.data?.detail || 'Ошибка запуска' });
      setRunning(false);
      return;
    }
    pollRef.current = setInterval(poll, 2000);
  }

  async function handleStop() {
    try { await automationAPI.autoArticleSyncStop(); } catch {}
  }

  const isDone    = status?.status === 'done';
  const isStopped = status?.status === 'stopped';
  const isError   = status?.status === 'error';

  return (
    <>
      {showInfo && (
        <InfoModal info={INFO_CONTENT.autoArticle} onClose={() => setShowInfo(false)} />
      )}

      <div className="automation-card">
        <div className="automation-card-header">
          <div className="automation-card-title-row">
            <span className="automation-card-title">Синхронизация ключевых слов фотопоиска</span>
            <button
              className="automation-info-btn"
              onClick={() => setShowInfo(true)}
              title="Подробная информация"
            >
              ⓘ
            </button>
          </div>
          <p className="automation-card-desc">
            Обходит таблицы поставщиков из «Список поставщиков», читает лист «Автоартикул»
            (столбец A — артикул, F — ключевое слово) и отправляет пары в API фотопоиска.
          </p>
        </div>

        <div className="automation-card-body">
          <div className="automation-card-actions">
            {!running ? (
              <button className="btn btn-primary" onClick={handleStart}>
                ▶ Запустить
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={handleStop}>
                ⏹ Остановить
              </button>
            )}
          </div>

          {status && (
            <div className="automation-card-result">
              {running && (
                <>
                  <div className="automation-progress-bar-wrap">
                    {status.total > 0 && (
                      <div
                        className="automation-progress-bar"
                        style={{ width: `${Math.round(((status.sent ?? 0) + (status.skipped ?? 0)) / status.total * 100)}%` }}
                      />
                    )}
                  </div>
                  <div className="automation-progress-text">{status.progress || '…'}</div>
                  <div className="automation-counters">
                    <span>Отправлено: <strong>{status.sent ?? 0}</strong></span>
                    <span>Пропущено: <strong>{status.skipped ?? 0}</strong></span>
                    {status.total > 0 && <span>Всего: <strong>{status.total}</strong></span>}
                  </div>
                </>
              )}

              {(isDone || isStopped) && (
                <div className="automation-result-ok">
                  {isStopped ? '⏹ ' : '✓ '}
                  {status.message}
                  {status.errors?.length > 0 && (
                    <details className="automation-errors">
                      <summary>⚠ {status.errors.length} ошибок</summary>
                      <ul>
                        {status.errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {isError && (
                <div className="automation-result-err">✗ {status.message}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Главный компонент ────────────────────────────────────────────────────────

export default function AutomationTab() {
  return (
    <div className="automation-tab">
      <h2 className="admin-section-title" style={{ marginBottom: 16 }}>Автоматизация</h2>
      <div className="automation-cards">
        <AutoArticleCard />
      </div>
    </div>
  );
}
